import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { customerService } from '../../../services/customerService';

const CUSTOMER_COLUMNS = [
  'company_name', 'contact_person', 'email', 'phone', 'address'
];

const parseCSV = (text) => {
  const lines = text?.trim()?.split('\n');
  if (lines?.length < 2) return { headers: [], rows: [] };
  const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.replace(/^"|"$/g, ''));
  const rows = lines?.slice(1)?.map(line => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line?.length; i++) {
      if (line?.[i] === '"') {
        inQuotes = !inQuotes;
      } else if (line?.[i] === ',' && !inQuotes) {
        values?.push(current?.trim());
        current = '';
      } else {
        current += line?.[i];
      }
    }
    values?.push(current?.trim());
    return headers?.reduce((obj, h, i) => {
      obj[h] = values?.[i] ?? '';
      return obj;
    }, {});
  });
  return { headers, rows };
};

const CustomerImportTab = () => {
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.name?.endsWith('.csv')) {
      setImportErrors(['Please select a valid CSV file.']);
      setImportPreview(null);
      setImportFile(null);
      return;
    }
    setImportFile(file);
    setImportResult(null);
    setImportErrors([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev?.target?.result;
      const { headers, rows } = parseCSV(text);
      const errors = [];
      if (!headers?.includes('company_name')) errors?.push('CSV must have a "company_name" column.');
      if (!headers?.includes('contact_person')) errors?.push('CSV must have a "contact_person" column.');
      if (!headers?.includes('email')) errors?.push('CSV must have an "email" column.');
      const validRows = rows?.filter(r => r?.company_name?.trim() && r?.contact_person?.trim() && r?.email?.trim());
      if (validRows?.length === 0) errors?.push('No valid rows found (company_name, contact_person, and email are required).');
      setImportErrors(errors);
      setImportPreview({ headers, rows: validRows?.slice(0, 5), total: validRows?.length, allRows: validRows });
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!importPreview || importErrors?.length > 0) return;
    setImporting(true);
    setImportResult(null);
    let successCount = 0;
    let failCount = 0;
    const rowErrors = [];
    for (const row of importPreview?.allRows) {
      const customerData = {
        companyName: row?.company_name?.trim(),
        contactPerson: row?.contact_person?.trim(),
        email: row?.email?.trim(),
        phone: row?.phone?.trim() || null,
        address: row?.address?.trim() || null,
      };
      try {
        const { error } = await customerService?.create(customerData);
        if (error) {
          failCount++;
          rowErrors?.push(`"${row?.company_name}": ${error?.message}`);
        } else {
          successCount++;
        }
      } catch (err) {
        failCount++;
        rowErrors?.push(`"${row?.company_name}": ${err?.message}`);
      }
    }
    setImportResult({ successCount, failCount, rowErrors });
    setImporting(false);
    if (successCount > 0) {
      setImportFile(null);
      setImportPreview(null);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const template = CUSTOMER_COLUMNS?.join(',') + '\nAcme Corp,John Smith,john@acme.com,555-123-4567,"123 Main St, Austin TX 78701"';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers_import_template.csv';
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleReset = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportErrors([]);
    setImportResult(null);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-8">
      {/* Header Info */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-blue-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Import Customers from CSV</h3>
            <p className="text-sm text-muted-foreground">Bulk import customers into the system from a CSV file</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            onClick={handleDownloadTemplate}
          >
            Download CSV Template
          </Button>
        </div>
        <div className="mt-4 p-3 bg-muted/40 rounded-lg">
          <p className="text-xs text-muted-foreground font-medium mb-1">Required columns:</p>
          <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">company_name</span>, <span className="font-semibold text-foreground">contact_person</span>, <span className="font-semibold text-foreground">email</span></p>
          <p className="text-xs text-muted-foreground mt-1">Optional columns: <span className="font-medium text-foreground">phone</span>, <span className="font-medium text-foreground">address</span></p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Upload" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Upload CSV File</h3>
            <p className="text-sm text-muted-foreground">Drag and drop or click to select your CSV file</p>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            importFile ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50 hover:bg-muted/30'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => e?.preventDefault()}
          onClick={() => fileInputRef?.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
          />
          {importFile ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Icon name="FileCheck" size={24} className="text-accent" />
              </div>
              <p className="font-medium text-foreground">{importFile?.name}</p>
              <p className="text-sm text-muted-foreground">{(importFile?.size / 1024)?.toFixed(1)} KB</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Icon name="Upload" size={24} className="text-muted-foreground" />
              </div>
              <p className="font-medium text-foreground">Drop your CSV file here</p>
              <p className="text-sm text-muted-foreground">or click to browse</p>
            </div>
          )}
        </div>

        {/* Validation Errors */}
        {importErrors?.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertCircle" size={16} className="text-red-600" />
              <p className="text-sm font-semibold text-red-700">Validation Errors</p>
            </div>
            <ul className="space-y-1">
              {importErrors?.map((err, i) => (
                <li key={i} className="text-sm text-red-600">• {err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Table */}
        {importPreview && importErrors?.length === 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Preview — showing {importPreview?.rows?.length} of {importPreview?.total} valid rows
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {importPreview?.total} customer{importPreview?.total !== 1 ? 's' : ''} ready to import
                </p>
              </div>
              <button
                onClick={(e) => { e?.stopPropagation(); handleReset(); }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Icon name="X" size={14} />
                Clear
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    {importPreview?.headers?.map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {h?.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {importPreview?.rows?.map((row, i) => (
                    <tr key={i} className="hover:bg-muted/30">
                      {importPreview?.headers?.map(h => (
                        <td key={h} className="px-3 py-2 text-foreground max-w-[200px] truncate">
                          {row?.[h] || <span className="text-muted-foreground italic">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {importPreview?.total > 5 && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                + {importPreview?.total - 5} more rows not shown in preview
              </p>
            )}
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`mt-4 p-4 rounded-lg border ${
            importResult?.failCount === 0
              ? 'bg-green-50 border-green-200'
              : importResult?.successCount === 0
              ? 'bg-red-50 border-red-200' :'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon
                name={importResult?.failCount === 0 ? 'CheckCircle' : importResult?.successCount === 0 ? 'XCircle' : 'AlertTriangle'}
                size={16}
                className={importResult?.failCount === 0 ? 'text-green-600' : importResult?.successCount === 0 ? 'text-red-600' : 'text-yellow-600'}
              />
              <p className={`text-sm font-semibold ${
                importResult?.failCount === 0 ? 'text-green-700' : importResult?.successCount === 0 ? 'text-red-700' : 'text-yellow-700'
              }`}>
                Import Complete
              </p>
            </div>
            <p className="text-sm text-foreground">
              {importResult?.successCount > 0 && (
                <span className="text-green-700 font-medium">{importResult?.successCount} customer{importResult?.successCount !== 1 ? 's' : ''} imported successfully. </span>
              )}
              {importResult?.failCount > 0 && (
                <span className="text-red-700 font-medium">{importResult?.failCount} row{importResult?.failCount !== 1 ? 's' : ''} failed.</span>
              )}
            </p>
            {importResult?.rowErrors?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {importResult?.rowErrors?.map((err, i) => (
                  <li key={i} className="text-xs text-red-600">• {err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {importPreview && importErrors?.length === 0 && !importResult && (
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              variant="default"
              iconName={importing ? 'Loader' : 'Upload'}
              iconPosition="left"
              onClick={(e) => { e?.stopPropagation(); handleImport(); }}
              disabled={importing}
            >
              {importing ? `Importing... (${importPreview?.total} rows)` : `Import ${importPreview?.total} Customer${importPreview?.total !== 1 ? 's' : ''}`}
            </Button>
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={(e) => { e?.stopPropagation(); handleReset(); }}
              disabled={importing}
            >
              Cancel
            </Button>
          </div>
        )}
        {importResult && importResult?.successCount > 0 && (
          <div className="mt-4">
            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              onClick={handleReset}
            >
              Import Another File
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerImportTab;
